import { useEffect, useState } from "react";
import axios from "axios";
import { Button, Modal, Form, Badge } from "react-bootstrap";
import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import Loading from "react-fullscreen-loading";
import { toast } from "react-hot-toast";
import { FaEdit, FaTrash, FaPlus, FaLock, FaUserShield } from "react-icons/fa";
import { useAuth } from "../../../authentication/AuthContext";
import "./index.css";

// ── Roles & permissions ───────────────────────────────────────────────────────
//
// Lists every role and lets an admin create one, rename it, tick the endpoints
// it may call, and delete it.
//
// The permission matrix is built from GET /api/admin/permissions, which serves
// the catalogue grouped portal → module → permission. The frontend deliberately
// does not know the key format: it renders whatever grouping the server sends,
// so adding an endpoint on the backend makes it appear here with no frontend
// change at all.
//
// Guard rails mirrored from the server (which enforces them regardless):
//   • is_super roles show their permissions as implicitly-all and read-only
//   • is_system roles cannot be deleted or have their portal changed
//   • you cannot edit your own role's permissions, or delete the role you hold
//   • a role with users cannot be deleted

const API = process.env.REACT_APP_API_URL;
const authHeaders = () => ({ "access-token": localStorage.getItem("accessToken") });

const USER_TYPE_LABELS = {
  admin: "Admin Portal",
  reseller: "Reseller Portal",
  user: "Customer Portal",
};

const USER_TYPE_COLORS = {
  admin: "#1e293b",
  reseller: "#523ba8",
  user: "#64748b",
};

export default function RolesIndex() {
  const { role: myRoleSlug, can, refreshPermissions } = useAuth();

  // admin.roles.index (which gates this page) means "may read the role list" — it
  // is also what the role filter on the users listing needs. Creating, editing and
  // deleting are separate permissions, so a role granted only index gets a
  // read-only view here instead of buttons that 403 on submit.
  const canCreate = can("admin.roles.create");
  const canUpdate = can("admin.roles.update");
  const canDelete = can("admin.roles.delete");

  const [screenloader, setScreenloader] = useState(true);
  const [roles, setRoles] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  // Total permission rows the server reports. Zero means the `permissions` table
  // is empty — i.e. scripts/sync_permissions.js has not been run against this
  // database — which is a deployment step, not a configuration choice.
  const [catalogueTotal, setCatalogueTotal] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);   // null = creating

  const [form, setForm] = useState({ name: "", description: "", user_type: "admin" });
  const [granted, setGranted] = useState(new Set());

  const loadRoles = async () => {
    const res = await axios.get(`${API}/admin/roles`, { headers: authHeaders() });
    if (res.data?.status) setRoles(res.data.roles || []);
    else toast.error(res.data?.message || "Could not load roles");
  };

  const loadCatalogue = async () => {
    const res = await axios.get(`${API}/admin/permissions`, { headers: authHeaders() });
    if (res.data?.status) {
      setCatalogue(res.data.portals || []);
      setCatalogueTotal(res.data.total ?? 0);
    } else {
      toast.error(res.data?.message || "Could not load permissions");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([loadRoles(), loadCatalogue()]);
      } catch (err) {
        if (err.response?.status !== 403) toast.error("Something went wrong loading roles");
      } finally {
        setScreenloader(false);
      }
    })();
  }, []);

  // ── Modal ──────────────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", user_type: "admin" });
    setGranted(new Set());
    setShowModal(true);
  };

  const openEdit = async (role) => {
    setScreenloader(true);
    try {
      const res = await axios.get(`${API}/admin/roles/${role.id}`, { headers: authHeaders() });
      if (!res.data?.status) {
        toast.error(res.data?.message || "Could not load role");
        return;
      }
      const full = res.data.role;
      setEditing(full);
      setForm({
        name: full.name,
        description: full.description || "",
        user_type: full.user_type,
      });
      setGranted(new Set(full.permissions || []));
      setShowModal(true);
    } catch {
      toast.error("Could not load role");
    } finally {
      setScreenloader(false);
    }
  };

  // A role's editor shows ONE table: the portal the role belongs to. An admin
  // role is configured against the admin portal, a reseller role against the
  // reseller portal, a customer role against the customer portal.
  //
  // Every role also holds the customer-portal keys — those endpoints scope
  // themselves to the caller's own records, so they are harmless to hold, and
  // all four seeded roles get them (which preserved the old behaviour where any
  // logged-in user could call them). They are not a decision anyone makes per
  // role, so putting a second table on screen only invited someone to break
  // one. They are held, not shown.
  //
  // Admin-portal permissions are deliberately NOT offered to reseller or customer
  // roles: those endpoints are unscoped, and handing one to a reseller would undo
  // the row-scoping split entirely.
  //
  // Hiding a portal does NOT drop its grants. `granted` is seeded from the
  // role's complete key list in openEdit and save() sends all of it, so what is
  // on screen decides what is *editable*, never what is kept.
  const visiblePortalIds = [form.user_type];
  const visiblePortals = catalogue.filter(p => visiblePortalIds.includes(p.portal));

  // Grants the role holds outside the table on screen. These are carried through
  // a save untouched, so this is information, not a warning — it exists so the
  // "N selected" count not matching the role's real total has a visible reason.
  const hiddenGrants = [...granted].filter(
    key => !visiblePortalIds.includes(String(key).split('.')[0])
  );

  const isSuperEditing = !!editing?.is_super;
  const isOwnRole = !!editing && editing.slug === myRoleSlug;
  // Read-only when: the role bypasses checks anyway, it is the caller's own role,
  // or the caller simply lacks permission to change roles.
  const isReadOnly = !!editing && !canUpdate;
  const permissionsLocked = isSuperEditing || isOwnRole || isReadOnly;

  const toggle = (key) => {
    if (permissionsLocked) return;
    setGranted(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleModule = (module) => {
    if (permissionsLocked) return;
    const keys = module.permissions.map(p => p.key);
    const allOn = keys.every(k => granted.has(k));
    setGranted(prev => {
      const next = new Set(prev);
      keys.forEach(k => (allOn ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Role name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        user_type: form.user_type,
      };
      // Omit permissions entirely when they cannot be edited, so the server
      // isn't asked to apply a change it will reject.
      if (!permissionsLocked) payload.permissions = [...granted];

      const res = editing
        ? await axios.put(`${API}/admin/roles/${editing.id}`, payload, { headers: authHeaders() })
        : await axios.post(`${API}/admin/roles`, payload, { headers: authHeaders() });

      if (!res.data?.status) {
        toast.error(res.data?.message || "Could not save role");
        return;
      }

      toast.success(res.data.message || "Saved");
      setShowModal(false);
      await loadRoles();
      // If we just changed a role, our own permission list may be affected —
      // pull it again so the sidebar reflects reality immediately.
      refreshPermissions();
    } catch (err) {
      if (err.response?.status === 403) toast.error("You are not authorized to manage roles.");
      else toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (role) => {
    if (!window.confirm(`Delete the role "${role.name}"? This cannot be undone.`)) return;
    try {
      const res = await axios.delete(`${API}/admin/roles/${role.id}`, { headers: authHeaders() });
      if (!res.data?.status) {
        toast.error(res.data?.message || "Could not delete role");
        return;
      }
      toast.success("Role deleted");
      await loadRoles();
    } catch (err) {
      if (err.response?.status === 403) toast.error("You are not authorized to delete roles.");
      else toast.error("Something went wrong");
    }
  };

  const grantedCountFor = (portal) =>
    portal.modules.reduce(
      (n, m) => n + m.permissions.filter(p => granted.has(p.key)).length,
      0
    );

  // AdminDashboardLayout renders the navbar and sidebar only — it does NOT render
  // children. Page content is a SIBLING of it, inside
  // content-page-bg > content-page > content. Wrapping content in the layout
  // silently renders nothing at all.
  return (
    <>
      <AdminDashboardLayout />
      <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
      <div className="content-page-bg">
        <div className="content-page">
          <div className="content">
            <div className="roles-page">
              <div className="roles-header">
                <div>
                  <h4 className="roles-title"><FaUserShield /> Roles &amp; Permissions</h4>
                  <p className="roles-subtitle">
                    A role decides which endpoints its members may call. Changes take
                    effect immediately, including for users who are already signed in.
                  </p>
                </div>
                {canCreate && (
                  <Button className="btn admin-btn" onClick={openCreate}>
                    <FaPlus /> New Role
                  </Button>
                )}
              </div>

              <div className="roles-grid">
                {roles.map(role => (
                  <div className="role-card" key={role.id}>
                    <div className="role-card-head">
                      <div>
                        <span className="role-name">{role.name}</span>
                        {!!role.is_super && (
                          <Badge bg="dark" className="role-badge" title="Bypasses every permission check">
                            Full access
                          </Badge>
                        )}
                        {!!role.is_system && (
                          <Badge bg="secondary" className="role-badge" title="Built-in role — cannot be deleted">
                            <FaLock size={9} /> Built-in
                          </Badge>
                        )}
                      </div>
                      <span
                        className="role-portal"
                        style={{ backgroundColor: USER_TYPE_COLORS[role.user_type] }}
                      >
                        {USER_TYPE_LABELS[role.user_type] || role.user_type}
                      </span>
                    </div>

                    {role.description && <p className="role-desc">{role.description}</p>}

                    <div className="role-stats">
                      <span>
                        <strong>{role.user_count}</strong> user{role.user_count === 1 ? "" : "s"}
                      </span>
                      <span>
                        {role.is_super
                          ? "all permissions"
                          : <><strong>{role.permission_count}</strong> permission{role.permission_count === 1 ? "" : "s"}</>}
                      </span>
                    </div>

                    <div className="role-actions">
                      <Button size="sm" variant="outline-primary" onClick={() => openEdit(role)}>
                        <FaEdit /> {(role.is_super || !canUpdate) ? "View" : "Edit"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        hidden={!canDelete}
                        disabled={!!role.is_system || role.user_count > 0}
                        title={
                          role.is_system
                            ? "Built-in roles cannot be deleted"
                            : role.user_count > 0
                              ? `${role.user_count} user(s) still have this role`
                              : "Delete role"
                        }
                        onClick={() => remove(role)}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? `Edit ${editing.name}` : "New Role"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="row">
            <div className="col-md-6 mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Marketing"
              />
            </div>
            <div className="col-md-6 mb-3">
              <Form.Label>Portal</Form.Label>
              <Form.Select
                value={form.user_type}
                disabled={!!editing?.is_system}
                onChange={e => {
                  const next = e.target.value;
                  setForm({ ...form, user_type: next });
                  // Admin- and reseller-portal grants make no sense under a
                  // different portal, so drop them. Customer-portal grants stay:
                  // they are valid for every role.
                  setGranted(prev => new Set(
                    [...prev].filter(k => String(k).startsWith('user.'))
                  ));
                }}
              >
                <option value="admin">Admin Portal</option>
                <option value="reseller">Reseller Portal</option>
                <option value="user">Customer Portal</option>
              </Form.Select>
              {!!editing?.is_system && (
                <Form.Text muted>A built-in role's portal cannot be changed.</Form.Text>
              )}
            </div>
            <div className="col-12 mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="What is this role for?"
              />
            </div>
          </div>

          {isReadOnly && !isSuperEditing && (
            <div className="roles-notice roles-notice-info">
              <FaLock /> You have read-only access to roles. Ask an administrator to
              grant you <code>admin.roles.update</code> to make changes.
            </div>
          )}

          {isSuperEditing && (
            <div className="roles-notice roles-notice-info">
              <FaLock /> <strong>{editing.name}</strong> has full access to everything by
              design, so it has no permission list to edit. This is what guarantees
              nobody can lock themselves out of this screen.
            </div>
          )}

          {isOwnRole && !isSuperEditing && (
            <div className="roles-notice roles-notice-warn">
              <FaLock /> This is your own role. You can rename it, but not change its
              permissions — otherwise you could revoke your own access to this page.
              Ask another administrator.
            </div>
          )}

          {!permissionsLocked && hiddenGrants.length > 0 && (
            <div className="roles-notice roles-notice-info">
              <FaLock /> This role also holds {hiddenGrants.length} permission
              {hiddenGrants.length === 1 ? "" : "s"} from{" "}
              {[...new Set(hiddenGrants.map(k => USER_TYPE_LABELS[k.split(".")[0]] || k.split(".")[0]))].join(", ")}
              . Those are self-scoped to each caller's own records, are not edited
              here, and are <strong>kept as they are</strong> when you save.
            </div>
          )}

          {!permissionsLocked && visiblePortals.map(portal => (
            <div className="perm-portal" key={portal.portal}>
              <div className="perm-portal-head">
                <span>{portal.label}</span>
                <span className="perm-count">{grantedCountFor(portal)} selected</span>
              </div>

              {portal.modules.map(module => {
                const keys = module.permissions.map(p => p.key);
                const on = keys.filter(k => granted.has(k)).length;
                const all = on === keys.length;
                return (
                  <div className="perm-module" key={module.module}>
                    <div className="perm-module-head">
                      <Form.Check
                        type="checkbox"
                        id={`mod-${portal.portal}-${module.module}`}
                        checked={all}
                        // Partially-selected modules show the indeterminate dash
                        // rather than reading as "none selected".
                        ref={el => { if (el) el.indeterminate = on > 0 && !all; }}
                        onChange={() => toggleModule(module)}
                        label={<strong>{module.label}</strong>}
                      />
                      <span className="perm-count">{on}/{keys.length}</span>
                    </div>
                    <div className="perm-items">
                      {module.permissions.map(p => (
                        <Form.Check
                          key={p.key}
                          type="checkbox"
                          id={p.key}
                          className="perm-item"
                          checked={granted.has(p.key)}
                          onChange={() => toggle(p.key)}
                          label={
                            <>
                              {p.name}
                              <code className="perm-route">{p.method} {p.path}</code>
                            </>
                          }
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {!permissionsLocked && visiblePortals.length === 0 && (
            catalogueTotal === 0 ? (
              // Distinguishes a missed deploy step from a genuinely empty portal.
              // Without this the screen just looks broken, and because the
              // Administrator role bypasses permission checks entirely, nothing
              // else in the app appears wrong.
              <div className="roles-notice roles-notice-warn">
                <FaLock />
                <span>
                  <strong>No permissions have been loaded into the database.</strong> The
                  permission list is generated from the code and written to the{" "}
                  <code>permissions</code> table by a deploy step that has not run yet on
                  this environment. On the server, in the backend directory, run:
                  <br />
                  <code>npm run permissions:seed</code>
                  <br />
                  Then reload this page. Use <code>npm run permissions:status</code> to
                  see which database is being read and what is in it.
                </span>
              </div>
            ) : (
              <div className="roles-notice roles-notice-info">
                No permissions are defined for this portal yet.
              </div>
            )
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            {isReadOnly ? "Close" : "Cancel"}
          </Button>
          {!isReadOnly && (
            <Button className="btn admin-btn" onClick={save} disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Create role"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
